/**
 * Shared topic autocomplete with question count display.
 * Used by QuestionDialog, MultiQuestionEditorDialog, and other forms to avoid duplicate code.
 */
import React from 'react';
import { Autocomplete, TextField } from '@mui/material';

export function getTopicOptionLabel(option, { includeQuestionCount = true } = {}) {
  if (!option) return '';
  const name = option.topic || option.name || '';
  if (!includeQuestionCount) return name;
  const count = option.question_count;
  return typeof count === 'number' ? `${name} (${count})` : name;
}

export function filterTopicOptions(options, inputValue) {
  if (!inputValue) return options;
  const searchTerm = inputValue.toLowerCase();
  const nameOnly = (opt) => (opt.topic || opt.name || '');
  return options.filter((option) =>
    nameOnly(option).toLowerCase().includes(searchTerm)
  );
}

const TopicAutocomplete = ({
  options = [],
  value,
  onChange,
  disabled = false,
  error = false,
  placeholder = 'Type to search topics...',
  label = 'Topic *',
  size = 'medium',
  fullWidth = true,
  showQuestionCount = true,
  ...rest
}) => (
  <Autocomplete
    options={options}
    getOptionLabel={(opt) => getTopicOptionLabel(opt, { includeQuestionCount: showQuestionCount })}
    value={options.find((t) => String(t.id) === String(value)) || null}
    onChange={(e, newValue) => onChange(newValue ? newValue.id : '')}
    disabled={disabled}
    fullWidth={fullWidth}
    filterOptions={(opts, { inputValue }) => filterTopicOptions(opts, inputValue)}
    renderInput={(params) => (
      <TextField
        {...params}
        label={label}
        error={error}
        placeholder={placeholder}
        size={size}
      />
    )}
    noOptionsText="No topics found"
    {...rest}
  />
);

/**
 * Multi-select topics; same filtering as TopicAutocomplete. Use showQuestionCount={false} to hide counts (e.g. exam prep).
 */
export function TopicsMultiAutocomplete({
  options = [],
  value = [],
  onChange,
  disabled = false,
  loading = false,
  label = 'Topics',
  placeholder = 'Type to search topics...',
  showQuestionCount = true,
  helperText = '',
  size = 'medium',
  fullWidth = true,
  ...rest
}) {
  return (
    <Autocomplete
      multiple
      options={options}
      value={value}
      onChange={(e, newValue) => onChange(newValue)}
      disabled={disabled || loading}
      fullWidth={fullWidth}
      getOptionLabel={(opt) => getTopicOptionLabel(opt, { includeQuestionCount: showQuestionCount }) || ''}
      isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
      filterOptions={(opts, { inputValue }) => filterTopicOptions(opts, inputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          helperText={loading ? 'Loading topics…' : helperText}
        />
      )}
      noOptionsText="No topics found"
      {...rest}
    />
  );
}

TopicsMultiAutocomplete.displayName = 'TopicsMultiAutocomplete';

TopicAutocomplete.displayName = 'TopicAutocomplete';
export default TopicAutocomplete;
export { TopicAutocomplete };
